"use client"

import React, { useState, useEffect, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import axios from "axios"
import {
  Container,
  Tabs,
  Tab,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  ListItem,
  ListItemText,
  Grid,
  Paper,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  CircularProgress,
  FormHelperText,
} from "@mui/material"
import {
  Assignment,
  RateReview,
  Feedback,
  Timeline,
  AccountTree,
  Person,
  Email,
  Send,
  Search,
  FilterList,
  Visibility,
} from "@mui/icons-material"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers"
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${API_URL}`,
  headers: {
    "Content-Type": "application/json",
  },
})

const ManagerSuccessionPlan = () => {
  const [successionPlans, setSuccessionPlans] = useState([])
  const [employees, setEmployees] = useState([])
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", content: "", action: null })
  const [loading, setLoading] = useState({
    succession: false,
    employees: false,
    submit: false,
  })

  // State for filtering plans by employee
  const [selectedEmployeeForView, setSelectedEmployeeForView] = useState("")

  const navigate = useNavigate()
  const location = useLocation()

  const [newSuccessionPlan, setNewSuccessionPlan] = useState({
    employee_id: "",
    employee_email: "",
    position: "",
    potentialSuccessors: "",
    readinessLevel: "",
    developmentNeeds: "",
    timeline: null,
  })

  useEffect(() => {
    fetchInitialData()
  }, [])

  // Auto-populate email when employee is selected - same pattern as goals/reviews
  useEffect(() => {
    if (newSuccessionPlan.employee_id) {
      const selectedEmployee = employees.find((emp) => emp.employee_id === newSuccessionPlan.employee_id)
      if (selectedEmployee) {
        setNewSuccessionPlan((prev) => ({
          ...prev,
          employee_email: selectedEmployee.email || "",
        }))
      }
    } else {
      setNewSuccessionPlan((prev) => ({
        ...prev,
        employee_email: "",
      }))
    }
  }, [newSuccessionPlan.employee_id, employees])

  const fetchInitialData = async () => {
    try {
      await Promise.all([fetchEmployees(), fetchPlans()])
    } catch (error) {
      //console.error("Error fetching initial data:", error)
    }
  }

  const fetchEmployees = async () => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    let employee_id = payload.employee_id;
    if (!employee_id) {
      try {
        const userRes = await api.get(`/api/user/current`, { headers: { Authorization: `Bearer ${token}` } });
        if (userRes.data && userRes.data.employee_id) employee_id = userRes.data.employee_id;
      } catch (e) {}
    }
      setLoading((prev) => ({ ...prev, employees: true }))
      const response = await api.get(`/api/succession-plans/employees?employee_id=${employee_id || ''}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          
          }
        }
      )

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid employee data received from server")
      }

      const employeeData = response.data

      const normalizedEmployees = employeeData.map((emp) => ({
        employee_id: emp.employee_id || emp.id || emp._id,
        firstName: emp.firstName || emp.first_name || emp.fname || "Unknown",
        lastName: emp.lastName || emp.last_name || emp.lname || "",
        email: emp.email || "",
        department: emp.department || emp.dept || "General",
        position: emp.position || emp.designation || emp.role || "",
      }))

      setEmployees(normalizedEmployees)
      return normalizedEmployees
    } catch (error) {
      //console.error("Error fetching employees:", error)
      handleSnackbarOpen(`Failed to load employees: ${error.message}`, "error")
      setEmployees([])
      return []
    } finally {
      setLoading((prev) => ({ ...prev, employees: false }))
    }
  }

  const fetchPlans = async () => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    let employee_id = payload.employee_id;
    if (!employee_id) {
      try {
        const userRes = await api.get(`/api/user/current`, { headers: { Authorization: `Bearer ${token}` } });
        if (userRes.data && userRes.data.employee_id) employee_id = userRes.data.employee_id;
      } catch (e) {}
    }
      setLoading((prev) => ({ ...prev, succession: true }))
      const response = await api.get(`/api/succession-plans?employee_id=${employee_id || ''}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
         
          }
        }
      )
      setSuccessionPlans(response.data || [])
      return response.data || []
    } catch (error) {
      //console.error("Error fetching plans:", error)
      handleSnackbarOpen(`Failed to load plans: ${error.message}`, "error")
      setSuccessionPlans([])
      return []
    } finally {
      setLoading((prev) => ({ ...prev, succession: false }))
    }
  }

  const handleTabChange = (event, newValue) => {
    navigate(newValue)
  }

  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity })
  }

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  const handleConfirmDialogOpen = (title, content, action) => {
    setConfirmDialog({ open: true, title, content, action })
  }

  const handleConfirmDialogClose = () => {
    setConfirmDialog({ ...confirmDialog, open: false })
  }

  const resetForm = () => {
    setNewSuccessionPlan({
      employee_id: "",
      employee_email: "",
      position: "",
      potentialSuccessors: "",
      readinessLevel: "",
      developmentNeeds: "",
      timeline: null,
    })
  }

  const validateForm = () => {
    const { employee_id, position, readinessLevel, developmentNeeds } = newSuccessionPlan
    return employee_id && position && readinessLevel && developmentNeeds
  }

  const getEmployeeName = (employeeId) => {
    const employee = employees.find((emp) => emp.employee_id === employeeId)
    if (!employee) return "Unknown Employee"

    const lastName = employee.lastName && employee.lastName.trim() ? employee.lastName : ""
    return lastName ? `${employee.firstName} ${lastName}` : employee.firstName
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      handleSnackbarOpen("Please fill all required fields", "error");
      return;
    }
  
    // Get token from localStorage
    const token = localStorage.getItem("token");
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    // Extract company_id from token
    const payload = JSON.parse(atob(token.split(".")[1]));
    const company_id = payload.company_id;
  
    try {
      setLoading((prev) => ({ ...prev, submit: true }));
  
      const payloadData = {
        ...newSuccessionPlan,
        potentialSuccessors: String(newSuccessionPlan.potentialSuccessors || ""),
      };
  
  
      const response = await api.post(
        `/api/succession-plans`,
        payloadData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
       
                "Content-Type": "application/json"
              
      
          },
        }
      );
  
  
      await fetchPlans(); // Refresh the data
      handleSnackbarOpen("Succession Plan created successfully and email notification sent!");
  
      resetForm();
    } catch (error) {
      //console.error("Error creating succession plan:", error.response?.data || error.message);
      handleSnackbarOpen(`Failed to create plan: ${error.response?.data?.error || error.message}`, "error");
    } finally {
      setLoading((prev) => ({ ...prev, submit: false }));
    }
  };
  

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified"
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString()
    } catch (e) {
      return String(dateString)
    }
  }

  // Filter plans based on selected employee for viewing
  const filteredPlansForDisplay = useMemo(() => {
    if (!successionPlans || successionPlans.length === 0) return []

    if (!selectedEmployeeForView) return []

    return successionPlans.filter((plan) => plan.employee_id === selectedEmployeeForView)
  }, [successionPlans, selectedEmployeeForView])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth={false} sx={{ maxWidth: 1500, p: 3, boxShadow: 3, mx: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" gutterBottom  fontWeight="bold">
            Performance Management 
          </Typography>
        </Box>

        <Paper elevation={3} sx={{ mb: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={location.pathname}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                backgroundColor: "#f5f5f5",
                "& .MuiTab-root": { fontWeight: "medium", py: 2 },
              }}
            >
              <Tab icon={<Assignment />} label="Goals & Objectives" value="/performance" />
              <Tab icon={<RateReview />} label="Performance Reviews" value="/review" />
              <Tab icon={<Feedback />} label="Feedback System" value="/feedback" />
              <Tab icon={<Timeline />} label="Improvement Plans" value="/improve" />
              <Tab icon={<AccountTree />} label="Succession Planning" value="/succession" />
              <Tab icon={<Visibility />} label="View Status" value="/performancestatus" />
            </Tabs>
          </Box>
        </Paper>

        {location.pathname === "/succession" && (
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              {/* Left side - Create Succession Plan */}
              <Grid item xs={12} md={4}>
                <Card elevation={3}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", color: "#1976d2" }}
                    >
                      <AccountTree sx={{ mr: 1 }} /> Create Succession Plan
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Tooltip title="Select the employee who currently holds this position" arrow>
                      <FormControl fullWidth margin="normal" required>
                        <InputLabel>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Person sx={{ mr: 0.5, fontSize: 16 }} />
                            Employee Name *
                          </Box>
                        </InputLabel>
                        <Select
                          value={newSuccessionPlan.employee_id}
                          onChange={(e) => setNewSuccessionPlan({ ...newSuccessionPlan, employee_id: e.target.value })}
                          label="Employee Name"
                          disabled={loading.employees}
                        >
                          <MenuItem value="">
                            <em>{loading.employees ? "Loading employees..." : "Select Employee"}</em>
                          </MenuItem>
                          {employees.length > 0
                            ? employees.map((employee) => (
                                <MenuItem key={employee.employee_id} value={employee.employee_id}>
                                  {getEmployeeName(employee.employee_id)}
                                 
                                </MenuItem>
                              ))
                            : !loading.employees && (
                                <MenuItem disabled>
                                  <em>No employees found</em>
                                </MenuItem>
                              )}
                        </Select>
                        {loading.employees && <CircularProgress sx={{ mt: 1 }} size={20} />}
                      </FormControl>
                    </Tooltip>

                    {/* Employee Email Field */}
                    <Tooltip title="Company email address (automatically fetched from database)" arrow>
                      <TextField
                        fullWidth
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Email sx={{ mr: 0.5, fontSize: 16 }} />
                           Email
                          </Box>
                        }
                        value={newSuccessionPlan.employee_email}
                        margin="normal"
                        disabled
                        helperText="Email is automatically fetching based on employee name"
                        InputProps={{
                          style: { backgroundColor: "#f5f5f5" },
                        }}
                      />
                    </Tooltip>

                    <Tooltip title="Enter the title of the position for this succession plan" placement="top" arrow>
                      <TextField
                        fullWidth
                        label="Position"
                        value={newSuccessionPlan.position}
                        onChange={(e) => setNewSuccessionPlan({ ...newSuccessionPlan, position: e.target.value })}
                        margin="normal"
                        required
                      />
                    </Tooltip>

                    <Tooltip
                      title="List potential candidates who could fill this position in the future (separate multiple names with commas)"
                      placement="top"
                      arrow
                    >
                      <TextField
                        fullWidth
                        label="Potential Successors"
                        value={newSuccessionPlan.potentialSuccessors}
                        onChange={(e) =>
                          setNewSuccessionPlan({ ...newSuccessionPlan, potentialSuccessors: e.target.value })
                        }
                        margin="normal"
                        placeholder="Comma-separated list of candidates"
                      />
                    </Tooltip>

                    <Tooltip
                      title="Estimate how soon the successor(s) will be ready to assume the position"
                      placement="top"
                      arrow
                    >
                      <FormControl fullWidth margin="normal" required>
                        <InputLabel>Readiness Level</InputLabel>
                        <Select
                          value={newSuccessionPlan.readinessLevel}
                          onChange={(e) =>
                            setNewSuccessionPlan({ ...newSuccessionPlan, readinessLevel: e.target.value })
                          }
                          label="Readiness Level"
                        >
                          <MenuItem value="">
                            <em>Select Readiness</em>
                          </MenuItem>
                          <MenuItem value="Ready Now">Ready Now</MenuItem>
                          <MenuItem value="0-6 months">0-6 months</MenuItem>
                          <MenuItem value="6-12 months">6-12 months</MenuItem>
                          <MenuItem value="1-2 years">1-2 years</MenuItem>
                          <MenuItem value="2+ years">2+ years</MenuItem>
                        </Select>
                      </FormControl>
                    </Tooltip>

                    <Tooltip
                      title="Describe specific skills, training, or experiences that potential successors need to develop"
                      placement="top"
                      arrow
                    >
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Development Needs"
                        value={newSuccessionPlan.developmentNeeds}
                        onChange={(e) =>
                          setNewSuccessionPlan({ ...newSuccessionPlan, developmentNeeds: e.target.value })
                        }
                        margin="normal"
                        placeholder="Skills and experiences needed for potential successors..."
                        required
                      />
                    </Tooltip>

                    <DatePicker
                      label="Target Timeline"
                      value={newSuccessionPlan.timeline ? new Date(newSuccessionPlan.timeline) : null}
                      onChange={(date) =>
                        setNewSuccessionPlan({
                          ...newSuccessionPlan,
                          timeline: date ? date.toISOString().split("T")[0] : null,
                        })
                      }
                      renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                    />

                    <Box sx={{ mt: 3, textAlign: "center" }}>
                      <Tooltip title="Create succession plan and send email notification to employee" arrow>
                        <Button
                          variant="contained"
                          startIcon={loading.submit ? <CircularProgress size={20} /> : <Send />}
                          onClick={handleSubmit}
                          disabled={loading.submit}
                          sx={{
                            bgcolor: "#1976d2",
                            color: "white",
                            px: 4,
                            py: 1.5,
                            fontSize: "1rem",
                            fontWeight: "medium",
                          }}
                        >
                          {loading.submit ? "Creating..." : "Create Plan"}
                        </Button>
                      </Tooltip>
                      <FormHelperText sx={{ mt: 1, textAlign: "center", fontSize: "0.875rem" }}>
                        This will create the succession plan and send an email notification to the employee
                      </FormHelperText>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Right side - Employee Succession Plans */}
              <Grid item xs={12} md={8}>
                <Paper elevation={3}>
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      bgcolor: "#f5f5f5",
                    }}
                  >
                    <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
                      <Search sx={{ mr: 1 }} /> Employee Succession Plans
                      {selectedEmployeeForView && ` (${filteredPlansForDisplay.length})`}
                    </Typography>
                    <Tooltip title="Select an employee to view their succession plans" arrow>
                      <FormControl size="small" sx={{ minWidth: 280 }}>
                        <InputLabel>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <FilterList sx={{ mr: 0.5, fontSize: 16 }} />
                            Select Employee to View Plans
                          </Box>
                        </InputLabel>
                        <Select
                          value={selectedEmployeeForView}
                          onChange={(e) => setSelectedEmployeeForView(e.target.value)}
                          label="Select Employee to View Plans"
                        >
                          <MenuItem value="">
                            <em>Choose an employee</em>
                          </MenuItem>
                          {employees.map((employee) => (
                            <MenuItem key={employee.employee_id} value={employee.employee_id}>
                              {getEmployeeName(employee.employee_id)}
                             
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Tooltip>
                  </Box>

                  <Divider />

                  {loading.succession ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Loading succession plans...
                      </Typography>
                    </Box>
                  ) : !selectedEmployeeForView ? (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                      <Search sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Select an Employee
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Choose an employee from the dropdown above to view their succession plans.
                      </Typography>
                    </Box>
                  ) : filteredPlansForDisplay.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <Typography variant="body1">
                        No succession plans found for {getEmployeeName(selectedEmployeeForView)}.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: "650px", overflow: "auto", p: 0 }}>
                      {filteredPlansForDisplay.map((plan) => (
                        <React.Fragment key={plan.id}>
                          <Paper elevation={2} sx={{ m: 2, p: 2, "&:hover": { bgcolor: "#f9f9f9" } }}>
                            <ListItem>
                              <ListItemText
                                primary={
                                  <Typography variant="subtitle1" fontWeight="medium">
                                    <strong>Employee Name:</strong> {getEmployeeName(plan.employee_id)} 
                                  </Typography>
                                  
                                }
                                secondary={
                                  <React.Fragment>
                                    <Grid container spacing={1} sx={{ mt: 1 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" component="span" color="textPrimary">
                                          <strong>Position:</strong> {plan.position}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" component="span" color="textPrimary" sx={{ml:17}}>
                                          <strong>Timeline:</strong> {formatDate(plan.timeline)}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" component="span" color="textPrimary" >
                                          <strong>Readiness:</strong> {plan.readinessLevel}
                                        </Typography>
                                      </Grid>
                                     
                                      <Grid item xs={12}>
                                        <Typography variant="body2" color="textPrimary">
                                          <strong>Potential Successors:</strong>{" "}
                                          {plan.potentialSuccessors || "None specified"}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Typography variant="body2" color="textPrimary">
                                          <strong>Development Needs:</strong> {plan.developmentNeeds}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </React.Fragment>
                                }
                              />
                            </ListItem>
                          </Paper>
                        </React.Fragment>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialog.open} onClose={handleConfirmDialogClose}>
          <DialogTitle>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{confirmDialog.content}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmDialogClose}>Cancel</Button>
            <Button onClick={confirmDialog.action} color="error" autoFocus>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  )
}

export default ManagerSuccessionPlan